import * as Yup from "yup";
import { Helmet } from "react-helmet-async";
import { useState } from "react";
// @mui
import { LoadingButton } from "@mui/lab";
import {
  Container,
  Grid,
  Card,
  Stack,
  Typography,
  Box,
  Divider,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
} from "@mui/material";
// components
import { useSettingsContext } from "../components/settings";
import { useSnackbar } from "../components/snackbar";
import FormProvider, { RHFTextField, RHFSelect } from "../components/hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { getNewNutritionTargetDetails, getUserCheckInDetails, getUserWeightEma8Details } from "../controllers/checkin";
import moment from "moment";

// Types
export type Level = "Beginner Phase 1" | "Beginner Phase 2" | "Intermediate";
export type Gender = "male" | "female";
export type Goal = "Lose Fat" | "Build Muscle" | "Maintain";

export interface ResultsData {
  recommendedCalorieTarget: number;
  nutritionActionResult: {
    carbMax: number;
    carbMin: number;
    fatMax: number;
    fatMin: number;
    lbsChangePerWeek: number;
    proteinMax: number;
    proteinMin: number;
    proteinTarget: number;
    rmr: number;
    tdee: number;
  };
  tdeeValue: number;
  confidenceLevel: number;
  dailyWeightChangeTarget: number;
  calorieTolerance: number;
  averageDailyCaloriesIntake: number;
  currentCalorieTarget: number;
  dailyChangeValue: number;
  updatedCalorieTarget: number;
};

export interface CheckInFormData {
  weight1: number;
  weight2: number;
  weight3: number;
  weight4: number;
  weight5: number;
  weight6: number;
  weight7: number;
  calorieIntake1: number;
  calorieIntake2: number;
  calorieIntake3: number;
  calorieIntake4: number;
  calorieIntake5: number;
  calorieIntake6: number;
  calorieIntake7: number;
  weightEma1: number;
  weightEma2: number;
  weightEma3: number;
  weightEma4: number;
  weightEma5: number;
  weightEma6: number;
  weightEma7: number;
  currentCalorieTarget: number;
  gender: Gender;
  level: Level;
  goal: Goal;
  bodyFat: number;
  heightCm: number;
}

export default function CheckIn() {
  const { themeStretch } = useSettingsContext();
  const { enqueueSnackbar } = useSnackbar();
  const [results, setResults] = useState<ResultsData | null>(null);
  const [userId, setUserId] = useState("");
  const [searchResults, setSearchResults] = useState<(CheckInFormData & any)[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const defaultValues: CheckInFormData = {
    weight1: 0,
    weight2: 0,
    weight3: 0,
    weight4: 0,
    weight5: 0,
    weight6: 0,
    weight7: 0,
    calorieIntake1: 0,
    calorieIntake2: 0,
    calorieIntake3: 0,
    calorieIntake4: 0,
    calorieIntake5: 0,
    calorieIntake6: 0,
    calorieIntake7: 0,
    weightEma1: 0,
    weightEma2: 0,
    weightEma3: 0,
    weightEma4: 0,
    weightEma5: 0,
    weightEma6: 0,
    weightEma7: 0,
    currentCalorieTarget: 0,
    gender: "male",
    level: "Beginner Phase 1",
    goal: "Maintain",
    bodyFat: 0,
    heightCm: 0,
  };

  const CheckInSchema = Yup.object().shape({
    weight1: Yup.number(),
    weight2: Yup.number(),
    weight3: Yup.number(),
    weight4: Yup.number(),
    weight5: Yup.number(),
    weight6: Yup.number(),
    weight7: Yup.number(),
    calorieIntake1: Yup.number(),
    calorieIntake2: Yup.number(),
    calorieIntake3: Yup.number(),
    calorieIntake4: Yup.number(),
    calorieIntake5: Yup.number(),
    calorieIntake6: Yup.number(),
    calorieIntake7: Yup.number(),
    weightEma1: Yup.number(),
    weightEma2: Yup.number(),
    weightEma3: Yup.number(),
    weightEma4: Yup.number(),
    weightEma5: Yup.number(),
    weightEma6: Yup.number(),
    weightEma7: Yup.number(),
    currentCalorieTarget: Yup.number(),
    gender: Yup.string().oneOf(["male", "female"]),
    level: Yup.string().oneOf(["Beginner Phase 1", "Beginner Phase 2", "Intermediate"]),
    goal: Yup.string().oneOf(["Lose Fat", "Build Muscle", "Maintain"]),
    bodyFat: Yup.number(),
    heightCm: Yup.number(),
  });

  const methods = useForm({
    resolver: yupResolver(CheckInSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (data: CheckInFormData) => {
    try {
      const response = await getNewNutritionTargetDetails(data);
      setResults({
        ...response.data
      });
    } catch (error) {
      console.error("Error submitting check-in data:", error);
      enqueueSnackbar("Failed to submit check-in data", { variant: "error" });
      setResults(null);
    }
  };

  const handleUserIdSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId.trim()) {
      enqueueSnackbar("Please enter a user ID", { variant: "error" });
      return;
    }

    setSearchLoading(true);
    try {
      const [response, ema8DataResponse] = await Promise.all([
        getUserCheckInDetails(userId),
        getUserWeightEma8Details(userId)
      ]);
      const ema8Data = ema8DataResponse.data || {};

      setSearchResults(response.data);

      if (!response.data || response.data.nutritionActionResults.length === 0) {
        setSearchResults([]);
        enqueueSnackbar("No check-in records found for this user", { variant: "info" });
      } else {
        const results = { ...response.data };
        const transformedRecords: (CheckInFormData & any)[] = [];
        const { gender, level, heightCm, bodyfat } = results.user;
        const { mealLogData, weightLogData } = results;
        // Using mealLogData, weightLogData, and to define the form data for prefill options
        results.nutritionActionResults.forEach((record: any) => {
          const date = record.createdOn;
          // filter the dates for the last 7 days from the record date excluding the record date itself
          transformedRecords.push({
            ...record,
            weight0: weightLogData[moment(date, "YYYY-MM-DD").subtract(8, "days").format("YYYY-MM-DD")] || 0,
            weight1: weightLogData[moment(date, "YYYY-MM-DD").subtract(7, "days").format("YYYY-MM-DD")] || 0,
            weight2: weightLogData[moment(date, "YYYY-MM-DD").subtract(6, "days").format("YYYY-MM-DD")] || 0,
            weight3: weightLogData[moment(date, "YYYY-MM-DD").subtract(5, "days").format("YYYY-MM-DD")] || 0,
            weight4: weightLogData[moment(date, "YYYY-MM-DD").subtract(4, "days").format("YYYY-MM-DD")] || 0,
            weight5: weightLogData[moment(date, "YYYY-MM-DD").subtract(3, "days").format("YYYY-MM-DD")] || 0,
            weight6: weightLogData[moment(date, "YYYY-MM-DD").subtract(2, "days").format("YYYY-MM-DD")] || 0,
            weight7: weightLogData[moment(date, "YYYY-MM-DD").subtract(1, "days").format("YYYY-MM-DD")] || 0,
            weight8: weightLogData[moment(date, "YYYY-MM-DD").subtract(0, "days").format("YYYY-MM-DD")] || 0,
            calorieIntake0: mealLogData[moment(date, "YYYY-MM-DD").subtract(8, "days").format("YYYY-MM-DD")] || 0,
            calorieIntake1: mealLogData[moment(date, "YYYY-MM-DD").subtract(7, "days").format("YYYY-MM-DD")] || 0,
            calorieIntake2: mealLogData[moment(date, "YYYY-MM-DD").subtract(6, "days").format("YYYY-MM-DD")] || 0,
            calorieIntake3: mealLogData[moment(date, "YYYY-MM-DD").subtract(5, "days").format("YYYY-MM-DD")] || 0,
            calorieIntake4: mealLogData[moment(date, "YYYY-MM-DD").subtract(4, "days").format("YYYY-MM-DD")] || 0,
            calorieIntake5: mealLogData[moment(date, "YYYY-MM-DD").subtract(3, "days").format("YYYY-MM-DD")] || 0,
            calorieIntake6: mealLogData[moment(date, "YYYY-MM-DD").subtract(2, "days").format("YYYY-MM-DD")] || 0,
            calorieIntake7: mealLogData[moment(date, "YYYY-MM-DD").subtract(1, "days").format("YYYY-MM-DD")] || 0,
            calorieIntake8: mealLogData[moment(date, "YYYY-MM-DD").subtract(0, "days").format("YYYY-MM-DD")] || 0,
            weightEma0: ema8Data[moment(date, "YYYY-MM-DD").subtract(8, "days").format("YYYY-MM-DD")] || 0,
            weightEma1: ema8Data[moment(date, "YYYY-MM-DD").subtract(7, "days").format("YYYY-MM-DD")] || 0,
            weightEma2: ema8Data[moment(date, "YYYY-MM-DD").subtract(6, "days").format("YYYY-MM-DD")] || 0,
            weightEma3: ema8Data[moment(date, "YYYY-MM-DD").subtract(5, "days").format("YYYY-MM-DD")] || 0,
            weightEma4: ema8Data[moment(date, "YYYY-MM-DD").subtract(4, "days").format("YYYY-MM-DD")] || 0,
            weightEma5: ema8Data[moment(date, "YYYY-MM-DD").subtract(3, "days").format("YYYY-MM-DD")] || 0,
            weightEma6: ema8Data[moment(date, "YYYY-MM-DD").subtract(2, "days").format("YYYY-MM-DD")] || 0,
            weightEma7: ema8Data[moment(date, "YYYY-MM-DD").subtract(1, "days").format("YYYY-MM-DD")] || 0,
            weightEma8: ema8Data[moment(date, "YYYY-MM-DD").subtract(0, "days").format("YYYY-MM-DD")] || 0,
            currentCalorieTarget: record.calorieTarget,
            gender,
            level,
            goal: record.goal,
            bodyFat: bodyfat,
            heightCm,
          });
        });
        setSearchResults(transformedRecords);
        enqueueSnackbar(`Found ${results.nutritionActionResults.length} record(s)`, { variant: "success" });
      }
    } catch (error) {
      console.error("Error fetching user check-in details:", error);
      enqueueSnackbar("Failed to fetch user check-in details", { variant: "error" });
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handlePrefill = (data: CheckInFormData) => {
    // Take the data and reset the form with it
    reset(data);
    // Scroll to top form
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Helmet>
        <title>Check In | Admin Portal</title>
      </Helmet>

      <Container maxWidth={themeStretch ? false : "xl"}>
        <Typography variant="h3" component="h1" paragraph>
          Dry Run CheckIn
        </Typography>

        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card sx={{ p: 3 }}>
                <Stack spacing={3}>
                  <Typography variant="h6">7-Day Data</Typography>

                  {/* Day Headers */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box sx={{ width: 120, flexShrink: 0 }} />
                    {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                      <Box key={day} sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Day {day}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Weight Row */}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Box sx={{ width: 120, flexShrink: 0 }}>
                      <Typography variant="body2" fontWeight="medium">
                        Weight (lbs)
                      </Typography>
                    </Box>
                    {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                      <Box key={day} sx={{ flex: 1 }}>
                        <RHFTextField
                          name={`weight${day}`}
                          type="number"
                          size="small"
                        />
                      </Box>
                    ))}
                  </Box>

                  {/* Weight EMA8 Row */}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Box sx={{ width: 120, flexShrink: 0 }}>
                      <Typography variant="body2" fontWeight="medium">
                        Weight EMA8
                      </Typography>
                    </Box>
                    {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                      <Box key={day} sx={{ flex: 1 }}>
                        <RHFTextField
                          name={`weightEma${day}`}
                          type="number"
                          size="small"
                        />
                      </Box>
                    ))}
                  </Box>

                  {/* Calorie Intake Row */}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Box sx={{ width: 120, flexShrink: 0 }}>
                      <Typography variant="body2" fontWeight="medium">
                        Calorie Intake
                      </Typography>
                    </Box>
                    {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                      <Box key={day} sx={{ flex: 1 }}>
                        <RHFTextField
                          name={`calorieIntake${day}`}
                          type="number"
                          size="small"
                        />
                      </Box>
                    ))}
                  </Box>

                  <Typography variant="h6" sx={{ mt: 2 }}>
                    Additional Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <RHFTextField
                        name="currentCalorieTarget"
                        label="Current Calorie Target"
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <RHFSelect name="gender" label="Gender">
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </RHFSelect>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <RHFSelect name="level" label="Level">
                        <option value="Beginner Phase 1">Beginner Phase 1</option>
                        <option value="Beginner Phase 2">Beginner Phase 2</option>
                        <option value="Intermediate">Intermediate</option>
                      </RHFSelect>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <RHFSelect name="goal" label="Goal">
                        <option value="Lose Fat">Lose Fat</option>
                        <option value="Build Muscle">Build Muscle</option>
                        <option value="Maintain">Maintain</option>
                      </RHFSelect>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <RHFTextField
                        name="bodyFat"
                        label="Body Fat %"
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <RHFTextField
                        name="heightCm"
                        label="Height (cm)"
                        type="number"
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </Card>

              <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                <LoadingButton
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  loading={isSubmitting}
                >
                  Submit
                </LoadingButton>
              </Stack>
            </Grid>

            {/* Results Panel */}
            <Grid item xs={12} md={4}>
              {results ? (
                <Card sx={{ p: 3 }}>
                  <Typography variant="h5" gutterBottom>
                    Results
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  <Stack spacing={2.5}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Recommended Calorie Target
                      </Typography>
                      <Typography variant="h4" color="primary.main">
                        {results.recommendedCalorieTarget} ± {results.calorieTolerance} <Typography variant="h6" color="text.secondary">({results.recommendedCalorieTarget - results.currentCalorieTarget < 0 ? "-" : "+"}{Math.abs(results.currentCalorieTarget - results.recommendedCalorieTarget)}) (Algo: {results.updatedCalorieTarget})</Typography>
                      </Typography>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        TDEE Value
                      </Typography>
                      <Typography variant="h6">
                        {results.tdeeValue ? results.tdeeValue.toFixed(2) : "INVALID"} <Typography variant="h6" color="text.secondary">({results.averageDailyCaloriesIntake ? results.averageDailyCaloriesIntake.toFixed(2) : "INVALID"} - { results.dailyChangeValue ? results.dailyChangeValue.toFixed(2) : "INVALID"})</Typography>
                      </Typography>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Confidence Level
                      </Typography>
                      <Typography variant="h6">
                        {results.confidenceLevel} - { results.confidenceLevel == 0 ? "LOW" : results.confidenceLevel == 1 ? "MODERATE" : "HIGH"}
                      </Typography>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Daily Weight Change Target
                      </Typography>
                      <Typography variant="h6">
                        {results.dailyWeightChangeTarget}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              ) : (
                <Card sx={{ p: 3, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
                  <Typography variant="body1" color="text.secondary">
                    Submit the form to see results
                  </Typography>
                </Card>
              )}
            </Grid>
          </Grid>
        </FormProvider>

        <Divider sx={{ my: 5 }} />

        {/* User ID Search Form */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            Search user records to prefill
          </Typography>
          <form onSubmit={handleUserIdSubmit}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', maxWidth: 800 }}>
              <TextField
                label="User ID"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="Enter user ID"
                sx={{ flex: 1 }}
              />
              <LoadingButton
                type="submit"
                variant="contained"
                size="large"
                loading={searchLoading}
              >
                Submit
              </LoadingButton>
            </Box>
          </form>

          {/* Search Results Table */}
          { (searchResults || []).length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Note:</strong> Prefilled data is calculated based on the user's timezone. Due to timezone differences, the date-specific records (weight, calories, EMA values) may vary by up to one day from the displayed dates.
              </Alert>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Calorie Target</TableCell>
                      <TableCell>Goal</TableCell>
                      <TableCell>Weight Change</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(searchResults || []).map((row: any, index) => (
                      <TableRow key={row.createdOn + row.calorieTarget + index} hover>
                        <TableCell>{row.createdOn}</TableCell>
                        <TableCell>
                          {row.calorieTarget} 
                          {row.calorieTarget - (row.oldCalorieTarget || 0) !== 0 ? ` (${row.calorieTarget - (row.oldCalorieTarget || 0)})` : " (No Change)"}
                          {row.oldCalorieTarget ? "" : " (New)"}
                        </TableCell>
                        <TableCell>{row.goal}</TableCell>
                        <TableCell>
                          {(row.weightEma7 - row.weightEma1).toFixed(4)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handlePrefill(row)}
                          >
                            Prefill
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </Container>
    </>
  );
}
